import { ActorState, normalizeActorState } from "./ActorState";
import {
  InteractionProperty,
  InteractionPropertyType,
} from "./InteractionProperty";
import { CreateSetCamera, ISetCamera } from "./interactions/SetCamera";
import {
  createDefaultTake3DSequenceInteraction,
  ensureTake3DSequenceDefaults,
  ISequence,
} from "./interactions/SequenceModel";
import Take from "./Take";
import { PropTakeState } from "./PropTakeState";
import { ITransition } from "./interactions/Transition";
import { TakeAttachment } from "./TakeAttachment";
import { GetNewGuid } from "../helpers/GuidHelper";

const required3dProperties: InteractionProperty[] = [
  {
    id: "isStory",
    locId: "cinematic_mode",
    tooltipLocId: "tooltip_cinematic_mode",
    name: "Cinematic Mode",
    type: InteractionPropertyType.boolean,
    value: false,
  },
  {
    id: "transition",
    locId: "transition",
    name: "Transition",
    type: InteractionPropertyType.boolean,
    value: false,
  },
  {
    id: "useDefaultCamera",
    locId: "use_default_camera",
    name: "Use Default Camera",
    type: InteractionPropertyType.boolean,
    value: false,
  },
];

export default interface Take3D extends Take {
  interactionPointId?: string;
  actors: ActorState[];
  props: PropTakeState[];
  mainCamera: ISetCamera;
  transition: ITransition;
  sequence: ISequence;

  attachments: Record<string, TakeAttachment>; // propId, attachmentId
  situations: Record<string, string>; // takelocationId, situationInstanceId
}

export function getTake3DSequence(take?: Pick<Take3D, "sequence">) {
  return take?.sequence;
}

export function isTake3DTransitionEnabled(
  take?: Pick<Take3D, "properties">
) {
  return Boolean(
    take?.properties?.find((property) => property.id === "transition")?.value
  );
}

export function normalizeTake3D(take: Take3D): Take3D {
  const sequence = ensureTake3DSequenceDefaults(
    take.sequence ?? createDefaultTake3DSequenceInteraction()
  );

  return repairTake3DActorStates({
    ...take,
    isExpanded: take.isExpanded ?? false,
    actors: (take.actors ?? []).map(normalizeActorState),
    props: take.props ?? [],
    attachments: take.attachments ?? {},
    situations: take.situations ?? {},
    sequence,
    interactions: take.interactions ?? [],
    mainCamera: take.mainCamera ?? CreateSetCamera(),
  });
}

export function repairTake3DActorStates(take: Take3D): Take3D {
  const validActorIds = new Set(
    (take.actors ?? []).map((actorState) => actorState.actorId)
  );
  let hasChanges = false;

  const actors = (take.actors ?? []).map((actorState) => {
    const lookat = actorState.lookat ?? "";
    const isMissingLookat = actorState.lookat === undefined;
    const shouldClearLookat =
      isMissingLookat ||
      (lookat !== "" &&
        (lookat === actorState.actorId || !validActorIds.has(lookat)));

    if (!shouldClearLookat) return actorState;

    hasChanges = true;
    return {
      ...actorState,
      lookat: "",
    };
  });

  return hasChanges ? { ...take, actors } : take;
}

export function CreateTake3D(
  name: string,
  actors: ActorState[],
  props: PropTakeState[],
  transition: ITransition,
  situations: Record<string, string>,
  attachments: Record<string, TakeAttachment>,
  interactionPointId?: string,
  camera?: ISetCamera
): Take3D {
  return {
    id: GetNewGuid(),
    name: name,
    interactions: [],
    isExpanded: false,
    actors: actors.map(normalizeActorState),
    props: props,
    mainCamera: CreateSetCamera(camera),
    properties: required3dProperties,
    interactionPointId: interactionPointId,
    transition: transition,
    situations: situations,
    attachments: attachments,
    sequence: createDefaultTake3DSequenceInteraction(),
  };
}
